import {useUpmPlant} from '@hconnect/upm'
import {Button} from '@hm/fundamental'
import {
  mdiPencilOutline,
  mdiLockOpenOutline,
  mdiContentSaveOutline,
  mdiPlus,
  mdiPrinterOutline
} from '@mdi/js'
import {useRef, useState, Suspense} from 'react'
import {useFormContext, type UseFormReturn} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useReactToPrint} from 'react-to-print'

import {AddKilnShutdownActions} from './AddKilnShutdownActions'
import {EditKilnShutdownActions} from './EditKilnShutdownActions'

import {KilnShutdownPrintContent} from '@refra/modules/common/components/KilnShutdownPrint'
import {ShutdownStatus} from '@refra/modules/common/enums'
import {useKilns, useRefraModal} from '@refra/modules/common/providers'
import type {NewShutdown, ShutdownForm} from '@refra/modules/common/types'
import {HotspotModal} from '@refra/modules/refra-planning/components/KilnShutdownsCard/KilnShutdownsCardBody/KilnShutdownCard/KilnShutdownGanttChart'
import {useEditShutdownStatus} from '@refra/modules/refra-planning/hooks/mutations'
import {KilnShutdown} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface KilnShutdownActionsProps {
  isNewShutdown: boolean
  isDraftShutdown: boolean
  isEditMode: boolean
  isRowLayout?: boolean
  setIsEditMode: (isEditMode: boolean) => void
  kilnShutdown: KilnShutdown | NewShutdown
}

export const KilnShutdownActions: React.FC<KilnShutdownActionsProps> = ({
  isNewShutdown,
  isDraftShutdown,
  isEditMode,
  setIsEditMode,
  isRowLayout,
  kilnShutdown
}) => {
  const {t} = useTranslation()

  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})
  const {selectedKiln} = useKilns()
  const {openModal} = useRefraModal()
  const printRef = useRef<HTMLDivElement>(null)
  const [shouldRenderPrintContent, setShouldRenderPrintContent] = useState(false)

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: t('refraPlanning.kilnShutdown.report.name'),
    onAfterPrint: () => {
      setShouldRenderPrintContent(false)
    },
    pageStyle: `
    @page {
      size: landscape;
    }

    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  `
  })

  const {mutate: editShutdownStatus} = useEditShutdownStatus()

  const {
    formState: {defaultValues}
  }: UseFormReturn<ShutdownForm> = useFormContext()

  const onShutdownStatusChange = (status: ShutdownStatus) => {
    if (defaultValues?.id === undefined) return

    return editShutdownStatus({
      plantUpmId: upmPlant.upmId,
      kilnUpmId: selectedKiln.upmId,
      shutdownId: defaultValues.id,
      status
    })
  }

  const handlePrintClick = () => {
    setShouldRenderPrintContent(true)
  }

  if (isNewShutdown) {
    return <AddKilnShutdownActions isRowLayout={isRowLayout} />
  }

  if (isEditMode) {
    return <EditKilnShutdownActions setIsEditMode={setIsEditMode} />
  }

  return (
    <div className="flex gap-1">
      {shouldRenderPrintContent && kilnShutdown.id && (
        <div style={{position: 'absolute', left: '-9999px', top: 0, width: '100%', height: '100%'}}>
          <Suspense fallback={null}>
            <KilnShutdownPrintContent
              ref={printRef}
              shutdown={kilnShutdown}
              onReady={() => handlePrint()}
            />
          </Suspense>
        </div>
      )}
      {kilnShutdown.id && (
        <Button
          icon={mdiPrinterOutline}
          variant="tertiary"
          onClick={() => handlePrintClick()}
          data-cy="kiln_shutdown_card_print_button"
        >
          {t('common.print')}
        </Button>
      )}
      {isDraftShutdown ? (
        <>
          {kilnShutdown.id && (
            <Button
              icon={mdiPlus}
              variant="tertiary"
              onClick={() =>
                openModal(<HotspotModal shutdown={kilnShutdown} hotSpots={kilnShutdown.hotSpots} />)
              }
              data-cy="kiln_shutdown_card_add_hotspot_button"
            >
              {t('refraPlanning.kilnShutdown.addHotspot')}
            </Button>
          )}
          <Button
            icon={mdiPencilOutline}
            variant="tertiary"
            onClick={() => setIsEditMode(true)}
            data-cy="kiln_shutdown_card_edit_button"
          >
            {t('common.edit')}
          </Button>
          <Button
            icon={mdiContentSaveOutline}
            disabled={!defaultValues?.feedingDate}
            variant="tertiary"
            onClick={() => onShutdownStatusChange(ShutdownStatus.Finalized)}
            data-cy="kiln_shutdown_card_finish_shutdown_button"
          >
            {t('refraPlanning.kilnShutdown.finishShutdown')}
          </Button>
        </>
      ) : (
        <Button
          icon={mdiLockOpenOutline}
          variant="tertiary"
          onClick={() => onShutdownStatusChange(ShutdownStatus.Draft)}
          data-cy="kiln_shutdown_card_reopen_button"
        >
          {t('common.reopen')}
        </Button>
      )}
    </div>
  )
}
